
import { useState } from "react";
import { toast } from "sonner";
import { Investor, Contact } from "@/types/investor";
import { EmailRecord } from "@/types/email";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "@/integrations/supabase/client";
import { useEmailStorage } from "./useEmailStorage";

export const useSendEmail = (
  investor: Investor,
  selectedContactData: Contact | null,
  generatedEmail: string,
  onEmailSent?: () => void,
  setEmailSent?: (sent: boolean) => void
) => {
  const [isSending, setIsSending] = useState(false);
  const { saveEmailRecord } = useEmailStorage();
  
  const handleSendEmail = async () => {
    if (!selectedContactData) {
      toast.error("Please select a contact");
      return;
    }
    
    if (!generatedEmail.trim()) {
      toast.error("Please generate an email first");
      return;
    }
    
    setIsSending(true);
    
    try {
      // Create email record
      const newEmail: EmailRecord = {
        id: uuidv4(),
        investorId: investor.id,
        contactId: selectedContactData.id,
        subject: `Intro to ${investor.firm}`,
        body: generatedEmail,
        sentAt: new Date().toISOString(),
        sentVia: "manual",
        hasResponse: false,
        status: "sent",
        createdAt: new Date()
      };
      
      // Save using the useEmailStorage hook
      await saveEmailRecord(newEmail);
      
      // Update the state to reflect that email was sent
      if (setEmailSent) {
        setEmailSent(true);
      }
      
      toast.success("Email marked as sent");
      
      // Call the callback if provided
      if (onEmailSent) {
        onEmailSent();
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email");
    } finally {
      setIsSending(false);
    }
  };

  return {
    isSending,
    handleSendEmail
  };
};
